import {
	ref,
	reactive,
	shallowRef,
	computed,
	watch,
	inject,
	provide,
	type Component,
	type InjectionKey,
	type Raw,
	markRaw,
} from 'vue';

// --- types ---

// the four top-level dock regions
export type PanelRegion = 'left' | 'center' | 'right' | 'bottom';

// split direction inside a region
export type SplitDirection = 'horizontal' | 'vertical';

// drop zone positions when dragging over a panel content area
export type DropPosition = 'left' | 'right' | 'top' | 'bottom' | 'center';

// mode for closeMany — which tabs to close relative to the target
export type CloseMode = 'all' | 'others' | 'left' | 'right';

// progress: 0-100 for determinate, Infinity for indeterminate, null for none
type Progress = number | null;

// reactive state exposed by a panel component to the panel system
export type PanelHandle = {
	uri: string;
	title: string;
	visible: boolean;
	loadProgress: Progress;
	saveProgress: Progress;
	dirty: boolean;
	error: string | null;
	// lifecycle callbacks — set by the panel component
	onFocus: (() => void) | null;
	onBlur: (() => void) | null;
	onClose: (() => void) | null;
};

// a panel instance — one open tab inside a leaf node
export type Panel = {
	uri: string;
	title: string;
	icon?: Raw<Component>;
	// the vue component to render for this panel
	component: Raw<Component>;
	// arbitrary props passed to the component
	props?: Record<string, unknown>;
	// which leaf node this panel lives in (node id)
	nodeId: string;
	// which top-level region this panel's node belongs to
	region: PanelRegion;
	// which regions this panel can be moved to (undefined = all regions)
	allowedRegions?: PanelRegion[];
	// whether the panel can be closed by the user
	closable: boolean;
	// timestamp (ms) of the last time this panel was activated — used to derive active tab and global focus
	lastActive: number;
	// called when the panel is closed (for system-level cleanup, e.g. dispose models)
	onClose?: () => void;
};

// --- split tree ---

// a leaf node holds a tab group (like the old PanelContainer)
export type LeafNode = {
	type: 'leaf';
	id: string;
	panels: Panel[];
};

// a branch node splits space between children
export type BranchNode = {
	type: 'branch';
	id: string;
	direction: SplitDirection;
	children: SplitNode[];
	// fractional sizes (0-1), one per child, must sum to 1
	sizes: number[];
};

export type SplitNode = LeafNode | BranchNode;

// a region root holds the split tree for one dock region
export type RegionRoot = {
	region: PanelRegion;
	root: SplitNode;
};

// an opener resolves a uri to a panel definition
// return null to decline (let next opener try)
export type PanelOpener = {
	// uri scheme this opener handles, e.g. 'file', 'terminal'
	scheme: string;
	// higher priority openers are tried first (default 0)
	priority?: number;
	// default region to open into
	defaultRegion: PanelRegion;
	// restrict which regions panels from this opener can live in (undefined = all regions)
	allowedRegions?: PanelRegion[];
	// create a panel definition from a uri — return null to decline and let the next opener try
	resolve: (uri: string) => Omit<Panel, 'region' | 'nodeId' | 'lastActive'> | null;
};

// options for panels.open()
export type OpenOptions = {
	region?: PanelRegion;
	nodeId?: string;
	focus?: boolean;
};

// --- id generation ---

let nodeCounter = 0;
export const generateNodeId = (): string => `node-${++nodeCounter}-${Date.now().toString(36)}`;

// create a fresh leaf node
export const createLeaf = (id?: string): LeafNode => ({
	type: 'leaf',
	id: id ?? generateNodeId(),
	panels: [],
});

// --- tree utilities ---

// find a node by id in a tree, returning the node and its parent
export const findNode = (
	root: SplitNode,
	id: string,
): { node: SplitNode; parent: BranchNode | null; index: number } | null => {
	if (root.id === id) return { node: root, parent: null, index: 0 };
	if (root.type === 'branch') {
		for (let i = 0; i < root.children.length; i++) {
			const result = findNode(root.children[i]!, id);
			if (result) {
				if (result.parent === null) {
					return { node: result.node, parent: root, index: i };
				}
				return result;
			}
		}
	}
	return null;
};

// find the leaf node containing a panel by uri
export const findLeafByUri = (root: SplitNode, uri: string): LeafNode | null => {
	if (root.type === 'leaf') {
		return root.panels.some((p) => p.uri === uri) ? root : null;
	}
	for (const child of root.children) {
		const result = findLeafByUri(child, uri);
		if (result) return result;
	}
	return null;
};

// collect all leaf nodes in a tree
export const collectLeaves = (root: SplitNode): LeafNode[] => {
	if (root.type === 'leaf') return [root];
	const result: LeafNode[] = [];
	for (const child of root.children) {
		result.push(...collectLeaves(child));
	}
	return result;
};

// collect all panels from a tree
export const collectPanels = (root: SplitNode): Panel[] => {
	const leaves = collectLeaves(root);
	const result: Panel[] = [];
	for (const leaf of leaves) {
		result.push(...leaf.panels);
	}
	return result;
};

// clean up empty leaves and single-child branches after a panel removal
export const pruneTree = (root: SplitNode): SplitNode => {
	if (root.type === 'leaf') return root;

	// recurse into children first
	const prunedChildren: SplitNode[] = [];
	const prunedSizes: number[] = [];
	for (let i = 0; i < root.children.length; i++) {
		const child = pruneTree(root.children[i]!);
		// remove empty leaves
		if (child.type === 'leaf' && child.panels.length === 0) continue;
		prunedChildren.push(child);
		prunedSizes.push(root.sizes[i]!);
	}

	// no children left — return an empty leaf
	if (prunedChildren.length === 0) return createLeaf(root.id);

	// single child — collapse the branch
	if (prunedChildren.length === 1) return prunedChildren[0]!;

	// normalize sizes to sum to 1
	const total = prunedSizes.reduce((a, b) => a + b, 0);
	const normalizedSizes =
		total > 0 ? prunedSizes.map((s) => s / total) : prunedSizes.map(() => 1 / prunedSizes.length);

	return {
		...root,
		children: prunedChildren,
		sizes: normalizedSizes,
	};
};

// --- composable ---

export const usePanels = () => {
	const regions = ref<Record<PanelRegion, RegionRoot>>({
		left: { region: 'left', root: createLeaf() },
		center: { region: 'center', root: createLeaf() },
		right: { region: 'right', root: createLeaf() },
		bottom: { region: 'bottom', root: createLeaf() },
	});

	// incremented on every structural tree change (split, move, prune) —
	// used by WorkspaceLayout to force Teleport re-resolution when DOM targets are recreated
	const treeVersion = ref(0);

	// handle registry — panel components register their reactive state here
	const handles = reactive(new Map<string, PanelHandle>());

	// --- drag state ---
	// uri of the panel currently being dragged
	const draggingUri = ref<string | null>(null);
	// pixel width/height of the dragged tab plus gap
	const draggingOffset = ref(0);

	// --- active uri for a leaf node ---
	const activeUriForNode = (node: LeafNode): string | null => {
		let best: Panel | null = null;
		for (const panel of node.panels) {
			if (!best || panel.lastActive > best.lastActive) {
				best = panel;
			}
		}
		return best?.uri ?? null;
	};

	// --- active uri for a region (highest lastActive across all leaves in the region tree) ---
	const activeUriForRegion = (region: PanelRegion): string | null => {
		const panels = collectPanels(regions.value[region].root);
		let best: Panel | null = null;
		for (const panel of panels) {
			if (!best || panel.lastActive > best.lastActive) {
				best = panel;
			}
		}
		return best?.uri ?? null;
	};

	// flat list of all panels across all regions — used by WorkspaceLayout to render with Teleport
	const allPanels = computed<Panel[]>(() => {
		const result: Panel[] = [];
		for (const regionRoot of Object.values(regions.value)) {
			result.push(...collectPanels(regionRoot.root));
		}
		return result;
	});

	// the globally focused panel — highest lastActive across all regions
	const focusedPanel = computed<Panel | null>(() => {
		let best: Panel | null = null;
		for (const panel of allPanels.value) {
			if (!best || panel.lastActive > best.lastActive) {
				best = panel;
			}
		}
		return best ?? null;
	});

	// fire onFocus/onBlur when the globally focused panel changes
	watch(
		() => focusedPanel,
		(newPanel, oldPanel) => {
			const newUri = newPanel.value?.uri;
			const oldUri = oldPanel.value?.uri;
			if (oldUri) handles.get(oldUri)?.onBlur?.();
			if (newUri) {
				requestAnimationFrame(() => {
					handles.get(newUri)?.onFocus?.();
				});
			}
		},
		{
			deep: true,
			flush: 'post',
		},
	);

	// opener registry
	const openers = shallowRef<PanelOpener[]>([]);

	const registerOpener = (opener: PanelOpener) => {
		const list = [...openers.value, opener];
		list.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
		openers.value = list;
	};

	const getScheme = (uri: string): string => {
		const idx = uri.indexOf(':');
		return idx === -1 ? '' : uri.slice(0, idx);
	};

	// find a panel by uri across all regions
	const findPanel = (uri: string): { panel: Panel; leaf: LeafNode; region: PanelRegion } | null => {
		for (const [region, regionRoot] of Object.entries(regions.value) as [
			PanelRegion,
			RegionRoot,
		][]) {
			const leaf = findLeafByUri(regionRoot.root, uri);
			if (leaf) {
				const panel = leaf.panels.find((p) => p.uri === uri);
				if (panel) return { panel, leaf, region };
			}
		}
		return null;
	};

	// find the first leaf node in a region (used as default target for opening panels)
	const firstLeaf = (root: SplitNode): LeafNode => {
		if (root.type === 'leaf') return root;
		return firstLeaf(root.children[0]!);
	};

	// find a leaf node by id across all regions
	const findLeafById = (id: string): { leaf: LeafNode; region: PanelRegion } | null => {
		for (const [region, regionRoot] of Object.entries(regions.value) as [
			PanelRegion,
			RegionRoot,
		][]) {
			const result = findNode(regionRoot.root, id);
			if (result && result.node.type === 'leaf') {
				return { leaf: result.node, region };
			}
		}
		return null;
	};

	// open a panel by uri — the core API
	const open = (uri: string, options?: OpenOptions) => {
		const shouldFocus = options?.focus ?? true;

		// if already open, just focus it
		const existing = findPanel(uri);
		if (existing) {
			if (shouldFocus) {
				focus(uri);
			}
			return existing.panel;
		}

		// find an opener for this uri
		const scheme = getScheme(uri);
		const matching = openers.value.filter((o) => o.scheme === scheme);
		if (matching.length === 0) {
			console.warn(`[panels] no opener registered for scheme "${scheme}" (uri: ${uri})`);
			return null;
		}

		let definition: Omit<Panel, 'region' | 'nodeId' | 'lastActive'> | null = null;
		let opener: PanelOpener | null = null;
		for (const candidate of matching) {
			definition = candidate.resolve(uri);
			if (definition) {
				opener = candidate;
				break;
			}
		}

		if (!definition || !opener) {
			console.warn(`[panels] all openers for "${scheme}" declined uri: ${uri}`);
			return null;
		}

		const region = options?.region ?? opener.defaultRegion;
		if (opener.allowedRegions && !opener.allowedRegions.includes(region)) {
			console.warn(
				`[panels] region "${region}" is not allowed for uri "${uri}" (allowed: ${opener.allowedRegions.join(', ')})`,
			);
			return null;
		}

		// determine target leaf node
		let targetLeaf: LeafNode;
		if (options?.nodeId) {
			const found = findLeafById(options.nodeId);
			targetLeaf = found ? found.leaf : firstLeaf(regions.value[region].root);
		} else {
			targetLeaf = firstLeaf(regions.value[region].root);
		}

		const panel: Panel = {
			...definition,
			component: markRaw(definition.component),
			icon: definition.icon ? markRaw(definition.icon) : undefined,
			region,
			nodeId: targetLeaf.id,
			allowedRegions: opener.allowedRegions,
			lastActive: shouldFocus ? Date.now() : 0,
		};

		targetLeaf.panels.push(panel);

		// register a handle immediately so the tab bar can render status/close before the component mounts
		handles.set(
			uri,
			reactive({
				uri,
				title: definition.title,
				visible: false,
				loadProgress: null,
				saveProgress: null,
				dirty: false,
				error: null,
				onFocus: null,
				onBlur: null,
				onClose: null,
			}),
		);

		return panel;
	};

	// close a panel by uri
	const close = (uri: string) => {
		const found = findPanel(uri);
		if (!found) return;

		const { panel, leaf, region } = found;
		const idx = leaf.panels.findIndex((p) => p.uri === uri);
		if (idx === -1) return;

		leaf.panels.splice(idx, 1);

		// notify the handle, then call the system-level cleanup
		handles.get(uri)?.onClose?.();
		panel.onClose?.();
		handles.delete(uri);

		// prune the tree if the leaf is now empty
		const regionRoot = regions.value[region];
		const pruned = pruneTree(regionRoot.root);
		if (pruned !== regionRoot.root) {
			regionRoot.root = pruned;
			treeVersion.value++;
		}
	};

	// close all other closable panels in the same leaf node
	const closeMany = (uri: string, mode: CloseMode) => {
		const found = findPanel(uri);
		if (!found) return;
		const { leaf, region } = found;
		const pivot = leaf.panels.findIndex((p) => p.uri === uri);
		if (pivot === -1) return;
		const toClose = leaf.panels.filter((p, i) => {
			if (!p.closable) return false;
			if (mode === 'others') return i !== pivot;
			if (mode === 'left') return i < pivot;
			if (mode === 'right') return i > pivot;
			return true; // 'all'
		});
		for (const panel of toClose) {
			const idx = leaf.panels.findIndex((p) => p.uri === panel.uri);
			if (idx === -1) continue;
			leaf.panels.splice(idx, 1);
			handles.get(panel.uri)?.onClose?.();
			panel.onClose?.();
			handles.delete(panel.uri);
		}
		const regionRoot = regions.value[region];
		const pruned = pruneTree(regionRoot.root);
		if (pruned !== regionRoot.root) {
			regionRoot.root = pruned;
			treeVersion.value++;
		}
	};

	// focus a panel (make it active in its leaf node and globally)
	const focus = (uri: string) => {
		const found = findPanel(uri);
		if (!found) return;
		found.panel.lastActive = Date.now();
	};

	// move a panel to a new index within its leaf node
	const movePanel = (uri: string, toIndex: number) => {
		const found = findPanel(uri);
		if (!found) return;
		const { leaf } = found;
		const fromIndex = leaf.panels.findIndex((p) => p.uri === uri);
		if (fromIndex === -1 || fromIndex === toIndex) return;
		const panel = leaf.panels.splice(fromIndex, 1)[0]!;
		leaf.panels.splice(toIndex, 0, panel);
	};

	// move a panel to a different leaf node (for tab reordering across splits)
	const movePanelToNode = (uri: string, targetNodeId: string, toIndex?: number) => {
		const found = findPanel(uri);
		if (!found) return;
		const { panel, leaf: sourceLeaf, region: sourceRegion } = found;

		// find target leaf
		const targetResult = findLeafById(targetNodeId);
		if (!targetResult) return;
		const { leaf: targetLeaf, region: targetRegion } = targetResult;

		// check if the target region is allowed
		if (panel.allowedRegions && !panel.allowedRegions.includes(targetRegion)) return;

		// same node — just reorder
		if (sourceLeaf.id === targetLeaf.id) {
			if (toIndex !== undefined) {
				movePanel(uri, toIndex);
			}
			return;
		}

		// remove from source
		const idx = sourceLeaf.panels.findIndex((p) => p.uri === uri);
		if (idx === -1) return;
		sourceLeaf.panels.splice(idx, 1);

		// update panel metadata
		panel.nodeId = targetLeaf.id;
		panel.region = targetRegion;

		// insert into target
		if (toIndex !== undefined && toIndex >= 0 && toIndex <= targetLeaf.panels.length) {
			targetLeaf.panels.splice(toIndex, 0, panel);
		} else {
			targetLeaf.panels.push(panel);
		}

		focus(uri);

		// prune source region
		const sourceRoot = regions.value[sourceRegion];
		const pruned = pruneTree(sourceRoot.root);
		if (pruned !== sourceRoot.root) {
			sourceRoot.root = pruned;
		}
		treeVersion.value++;
	};

	// split a leaf node by creating a new leaf and inserting a panel into it
	const splitNode = (sourceUri: string, targetNodeId: string, position: DropPosition) => {
		if (position === 'center') {
			// just move the panel to the existing node
			movePanelToNode(sourceUri, targetNodeId);
			return;
		}

		const found = findPanel(sourceUri);
		if (!found) return;
		const { panel } = found;

		// find the target node and which region it's in
		let targetRegion: PanelRegion | null = null;
		let targetRegionRoot: RegionRoot | null = null;
		for (const [region, regionRoot] of Object.entries(regions.value) as [
			PanelRegion,
			RegionRoot,
		][]) {
			if (findNode(regionRoot.root, targetNodeId)) {
				targetRegion = region;
				targetRegionRoot = regionRoot;
				break;
			}
		}
		if (!targetRegion || !targetRegionRoot) return;

		// check if the target region is allowed
		if (panel.allowedRegions && !panel.allowedRegions.includes(targetRegion)) return;

		// determine split direction from drop position
		const direction: SplitDirection =
			position === 'left' || position === 'right' ? 'horizontal' : 'vertical';
		const insertBefore = position === 'left' || position === 'top';

		// remove panel from its current location
		const sourceResult = findPanel(sourceUri);
		if (!sourceResult) return;
		const { leaf: sourceLeaf, region: sourceRegion } = sourceResult;
		const idx = sourceLeaf.panels.findIndex((p) => p.uri === sourceUri);
		if (idx === -1) return;
		sourceLeaf.panels.splice(idx, 1);

		// create new leaf for the dropped panel
		const newLeaf = createLeaf();
		panel.nodeId = newLeaf.id;
		panel.region = targetRegion;
		newLeaf.panels.push(panel);

		// find the target node in the tree
		const targetInfo = findNode(targetRegionRoot.root, targetNodeId);
		if (!targetInfo) return;

		const { parent } = targetInfo;

		if (parent && parent.direction === direction) {
			// parent already splits in the same direction — add the new leaf as a sibling
			const targetIdx = parent.children.findIndex((c) => c.id === targetNodeId);
			const insertIdx = insertBefore ? targetIdx : targetIdx + 1;

			// split the target's size in half
			const targetSize = parent.sizes[targetIdx]!;
			const halfSize = targetSize / 2;
			parent.sizes[targetIdx] = halfSize;

			parent.children.splice(insertIdx, 0, newLeaf);
			parent.sizes.splice(insertIdx, 0, halfSize);
		} else {
			// need to create a new branch node wrapping the target
			const targetNode = targetInfo.node;
			const branch: BranchNode = {
				type: 'branch',
				id: generateNodeId(),
				direction,
				children: insertBefore ? [newLeaf, targetNode] : [targetNode, newLeaf],
				sizes: [0.5, 0.5],
			};

			// replace target node in the tree
			if (parent) {
				const childIdx = parent.children.findIndex((c) => c.id === targetNodeId);
				parent.children[childIdx] = branch;
			} else {
				// target was the root
				targetRegionRoot.root = branch;
			}
		}

		focus(sourceUri);

		// prune source region tree
		const sourceRoot = regions.value[sourceRegion];
		const pruned = pruneTree(sourceRoot.root);
		if (pruned !== sourceRoot.root) {
			sourceRoot.root = pruned;
		}
		treeVersion.value++;
	};

	// check if a panel can be dropped into a given region
	const canMoveToRegion = (uri: string, targetRegion: PanelRegion): boolean => {
		const found = findPanel(uri);
		if (!found) return false;
		const { panel } = found;
		if (!panel.allowedRegions) return true;
		return panel.allowedRegions.includes(targetRegion);
	};

	// resize a branch node's children
	const resizeSplit = (branchId: string, sizes: number[]) => {
		for (const regionRoot of Object.values(regions.value)) {
			const result = findNode(regionRoot.root, branchId);
			if (result && result.node.type === 'branch') {
				result.node.sizes = sizes;
				return;
			}
		}
	};

	// signal that a resize gesture finished — triggers persistence
	const commitResize = () => {
		treeVersion.value++;
	};

	return {
		regions,
		treeVersion,
		handles,
		draggingUri,
		draggingOffset,
		allPanels,
		activeUriForNode,
		activeUriForRegion,
		registerOpener,
		open,
		close,
		closeMany,
		focus,
		findPanel,
		findLeafById,
		movePanel,
		movePanelToNode,
		splitNode,
		canMoveToRegion,
		resizeSplit,
		commitResize,
		collectPanels,
		collectLeaves,
		firstLeaf,
	};
};

export type PanelSystem = ReturnType<typeof usePanels>;

// --- panel slot injection (provided by PanelSlot, consumed by usePanelHandle) ---

export const PANEL_URI_KEY: InjectionKey<string> = Symbol('panel-uri');
export const PANEL_SYSTEM_KEY: InjectionKey<PanelSystem> = Symbol('panel-system');

export const providePanelSlot = (panels: PanelSystem, uri: string) => {
	provide(PANEL_SYSTEM_KEY, panels);
	provide(PANEL_URI_KEY, uri);
};

// --- panel handle composable (used inside panel components) ---

export const usePanelHandle = (initialTitle?: string): PanelHandle => {
	const panels = inject(PANEL_SYSTEM_KEY);
	const uri = inject(PANEL_URI_KEY);
	if (!panels || !uri) {
		throw new Error('usePanelHandle() must be called inside a panel rendered by PanelContainer');
	}

	const handle = panels.handles.get(uri);
	if (!handle) {
		throw new Error(
			`[panels] no handle found for uri "${uri}" — was the panel opened via panels.open()?`,
		);
	}

	// allow the panel component to override the title
	if (initialTitle !== undefined) {
		handle.title = initialTitle;
	}

	return handle;
};
